import type { BrainstormClarifyingQuestionsData } from '@frontend/api-types';
import { useState } from '@frontend/react';
import { Button, ClarifyingQuestionBlock, Text, XStack, YStack, showToast } from '@frontend/web-react';

type TaskChatClarifyingQuestionsProps = {
  data: BrainstormClarifyingQuestionsData;
  isDisabled?: boolean;
  onSubmit: (answers: Record<string, string[]>) => Promise<void> | void;
};

export const TaskChatClarifyingQuestions = ({ data, isDisabled, onSubmit }: TaskChatClarifyingQuestionsProps) => {
  const [selections, setSelections] = useState<Record<string, string[]>>({});
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const totalQuestions = data.questions.length;
  const currentQuestion = data.questions[currentQuestionIndex];
  const isLastQuestion = currentQuestionIndex === totalQuestions - 1;
  const isCurrentAnswered = (selections[String(currentQuestionIndex)] ?? []).length > 0;

  if (!currentQuestion) return null;

  const handleOptionClick = (questionIndex: number, optionLabel: string, isMultiSelect: boolean) => {
    const key = String(questionIndex);
    setSelections((prev) => {
      const current = prev[key] ?? [];
      if (isMultiSelect) {
        const isSelected = current.includes(optionLabel);
        return {
          ...prev,
          [key]: isSelected ? current.filter((l) => l !== optionLabel) : [...current, optionLabel],
        };
      }
      return { ...prev, [key]: [optionLabel] };
    });
  };

  const isOptionSelected = (questionIndex: number, optionLabel: string) =>
    (selections[String(questionIndex)] ?? []).includes(optionLabel);

  const allQuestionsAnswered = data.questions.every((_, idx) => (selections[String(idx)] ?? []).length > 0);

  const handleSubmit = async () => {
    if (allQuestionsAnswered && !isSubmitting) {
      setIsSubmitting(true);
      try {
        await onSubmit(selections);
      } catch {
        setIsSubmitting(false);
        showToast({ title: 'Failed to submit answers. Please try again.', variant: 'danger' });
      }
    }
  };

  const handleNext = () => {
    if (isCurrentAnswered && !isLastQuestion) {
      setCurrentQuestionIndex((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex((prev) => prev - 1);
    }
  };

  const getHeaderText = () => {
    if (totalQuestions === 1) return 'Quick question to help me find the best options:';
    if (currentQuestionIndex === 0) return 'Quick questions to help me find the best options:';
    if (isLastQuestion) return 'Almost there!';
    const remaining = totalQuestions - currentQuestionIndex - 1;
    return `${remaining} more ${remaining === 1 ? 'question' : 'questions'} after this:`;
  };

  return (
    <YStack className="gap-md py-md">
      <Text className="text-neutral-950" type="p-md">
        {getHeaderText()}
      </Text>
      {totalQuestions > 1 && (
        <Text color="tertiary" type="caption">
          Question {currentQuestionIndex + 1} of {totalQuestions}
        </Text>
      )}
      <ClarifyingQuestionBlock
        isDisabled={isDisabled}
        isOptionSelected={(label) => isOptionSelected(currentQuestionIndex, label)}
        onOptionClick={(label) => handleOptionClick(currentQuestionIndex, label, currentQuestion.multiSelect)}
        question={currentQuestion}
        questionNumber={currentQuestionIndex + 1}
      />
      <XStack className="justify-between pt-sm">
        {currentQuestionIndex > 0 ? (
          <Button label="Back" onClick={handleBack} size="sm" variant="secondary" />
        ) : (
          <span />
        )}
        {isLastQuestion ? (
          <Button
            className="px-lg"
            disabled={isDisabled || isSubmitting || !isCurrentAnswered}
            isLoading={isSubmitting}
            label="Submit"
            onClick={handleSubmit}
            size="sm"
            variant="primary"
          />
        ) : (
          <Button
            className="px-lg"
            disabled={isDisabled || !isCurrentAnswered}
            label="Next"
            onClick={handleNext}
            size="sm"
            variant="primary"
          />
        )}
      </XStack>
    </YStack>
  );
};
